import { useEffect, useRef } from "react"

/**
 * A CMS field rendered on the page that the inline editor can stamp client-side.
 * On production the public HTML ships **no** `data-cms-*` attrs; instead each
 * editable surface registers its provenance + container element here (in JS,
 * never in the DOM), and `EditModeHost` injects the stamps after a signed-in
 * user activates edit mode. Keeps the public build byte-clean while still
 * letting internal users edit on the live site. (AHD-7425)
 */
export interface EditRegionCoords {
  /** Payload collection slug, e.g. `"blog-posts"`. */
  collection: string
  /** Payload document id. */
  id: string
  /** Field name within the doc, e.g. `"content"`. */
  field: string
  /** BCP47 CMS locale the doc was fetched in. */
  locale: string
  /** Field shape — drives the injector and the editor modal. */
  kind: "scalar" | "html" | "lexical"
}

export interface EditRegion {
  el: HTMLElement
  coords: EditRegionCoords
  /** For kind "lexical": the in-memory content tree the page rendered, walked
   *  by the injector. Undefined for scalar/html (the element IS the field). */
  value?: unknown
}

const regions = new Set<EditRegion>()
const listeners = new Set<() => void>()

function notify(): void {
  listeners.forEach((l) => l())
}

/** Register a live editable region. Returns an unregister function. */
export function registerEditRegion(
  el: HTMLElement,
  coords: EditRegionCoords,
  value?: unknown
): () => void {
  const region: EditRegion = { el, coords, value }
  regions.add(region)
  notify()
  return () => {
    regions.delete(region)
    notify()
  }
}

/** Snapshot of the currently-registered regions. */
export function listEditRegions(): EditRegion[] {
  return Array.from(regions)
}

/** Subscribe to register/unregister changes. Returns an unsubscribe function. */
export function subscribeEditRegions(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/**
 * Attach an editable CMS region to a container element. Spread the returned ref
 * onto the element that wraps the rendered field; pass `null` coords when the
 * field has no id yet (nothing registers, no DOM markup either way).
 */
export function useEditRegion<T extends HTMLElement = HTMLElement>(
  coords: EditRegionCoords | null,
  value?: unknown
): React.RefObject<T | null> {
  const ref = useRef<T | null>(null)
  const { collection, id, field, locale, kind } = coords ?? {}
  useEffect(() => {
    const el = ref.current
    if (!el || !collection || !id || !field || !locale || !kind) return
    return registerEditRegion(
      el,
      { collection, id, field, locale, kind },
      value
    )
  }, [collection, id, field, locale, kind, value])
  return ref
}
